<template>
  <div class="upload">
    <Header />
    <el-form ref="form" :model="form" label-width="120px" :rules="rules">
      <h1 class="title"><span v-if="newUpload">New</span><span v-else>Edit</span> upload</h1>
      <h2 class="sectionTitle">Files</h2>
      <el-upload
        class="upload-demo"
        ref="upload"
        drag
        :action="uploadUrl"
        :on-change="handleUploadChange"
        :on-preview="handlePreview"
        :on-remove="handleRemove"
        :on-success="handleUploadSuccess"
        :on-error="handleUploadError"
        :before-remove="handleUploadBeforeRemove"
        :file-list="form.files"
        :auto-upload="false"
        multiple>
        <i class="el-icon-upload"></i>
        <div class="el-upload__text">Drop file here or <em>click to upload</em></div>
        <div class="el-upload__tip" slot="tip">
          Minimum 1 file required, files with a size less than 100MB, max 50GB per dataset
        </div>
      </el-upload>
      <div class="start-upload" v-if="filesToUpload > 0">
        <el-tooltip
          effect="dark"
          content="Otherwise upload will be automatically started when you save"
          placement="left">
          <el-button
            size="small"
            type="primary" @click="onStartUpload"
          >
            Start upload
          </el-button>
        </el-tooltip>
      </div>
      <h2 class="sectionTitle">Upload type</h2>
      <div class="uploadType">
        <el-radio v-for="type in uploadChoice"
          :key="type.label"
          class="uploadTypeChoice"
          v-model="form.uploadType" :label="type.value">{{type.label}}</el-radio>
      </div>
      <div class="uploadTypeBonus">
        <el-form-item class="no-left" prop="publicationType"
          v-if="form.uploadType === 'publication'">
          <label>
            Publication type:
            <el-select v-model="form.publicationType" placeholder="Select">
             <el-option
               v-for="type in publicationChoice"
               :key="type.value"
               :label="type.label"
               :value="type.value">
             </el-option>
           </el-select>
         </label>
      </el-form-item>
      <el-form-item class="no-left" prop="imageType" v-if="form.uploadType === 'image'">
        <label>
          Image type:
          <el-select v-model="form.imageType" placeholder="Select">
           <el-option
             v-for="type in imageChoice"
             :key="type.value"
             :label="type.label"
             :value="type.value">
           </el-option>
         </el-select>
       </label>
      </el-form-item>
      </div>
      <h2 class="sectionTitle">Basic information</h2>
      <div>
        <el-form-item label="Title" prop="title">
          <el-input placeholder="Add title" v-model="form.title"></el-input>
        </el-form-item>
        <el-form-item label="Authors" prop="creators">
          <div class="author" v-for="(author, index) in form.creators" :key="index">
            <el-input
              class="half"
              placeholder="Family name, given names"
              v-model="author.name" @change="handleChange(index)">
            </el-input>
            <el-input
              class="half"
              placeholder="Affiliation" v-model="author.affiliation" @change="handleChange(index)">
            </el-input>
            <button
              v-if="index < form.creators.length -1"
              type="button"
              class="removeCreator"
              @click="removeCreator(index)"><i class="el-icon-error" /></button>
          </div>
        </el-form-item>
        <el-form-item label="Description" prop="description">
          <el-input type="textarea"
            :autosize="{ minRows: 3}"
            placeholder="Add description"
            v-model="form.description"></el-input>
        </el-form-item>
        <el-form-item label="Keywords">
          <el-select
            class="fullWidth"
            v-model="form.keywords"
            multiple
            filterable
            placeholder="Choose keywords for your upload">
            <el-option-group
              v-for="group in availableKeywords"
              :key="group.label"
              :label="group.label">
              <el-option
                v-for="item in group.options"
                :key="item"
                :label="item"
                :value="item">
              </el-option>
            </el-option-group>
          </el-select>
        </el-form-item>
        <el-form-item label="Additional notes">
          <el-input type="textarea"
            :autosize="{ minRows: 3}"
            placeholder="Add notes"
            v-model="form.notes"></el-input>
            <span class="add-sentence">
              The following expression will be automatically added to the additional notes:
              <q class="sentence">
                This project has received funding from the EMPIR programme
                co-financed by the Participating States and from the European Union’s Horizon
                2020 research and innovation programme
              </q>
            </span>
        </el-form-item>
      </div>
      <div class="buttons">
        <div class="force-left">
          <el-button type="danger" @click="onDelete" v-if="!newUpload">Delete</el-button>
        </div>
        <el-button
        type="primary" @click="onSave"
        :disabled="!changed" :loading="saving" >Save</el-button>
        <el-button type="primary"
          :disabled="changed || newUpload || submitted" :loading="publishing" @click="confirmPublish">
          Publish
        </el-button>
      </div>

    </el-form>
    <Footer />
  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'

import Header from './Header.vue'
import Footer from './Footer.vue'

import availableKeywords from '../keywords.json'
import values from '../values.json'
import config from '../config.json'

import { constructConfig } from '../utils/api'

const emptyCreator = () => ({
  name: '',
  affiliation: '',
})

export default {
  name: 'Upload',
  props: {
    zenodoAuth: Object,
  },
  watch: {
    form: {
      handler(/* value, oldValue */) {
        if (
          !this.changed &&
          this.fetched &&
          JSON.stringify(this.form) !== JSON.stringify(this.initialValues)
        ) {
          this.changed = true
        }

        // should be the case everytime
        if (this.form.creators.length >= 1) {
          const lastCreator = this.form.creators[this.form.creators.length - 1]
          if (lastCreator.name !== '' || lastCreator.affiliation !== '') {
            this.form.creators.push(emptyCreator())
          }
        }

        // this.needToUpload = this.form.files.some(file => (file.status === 'ready'))
      },
      deep: true,
    },
  },
  data() {
    return {
      submitted: false,
      initialValues: {},
      newUpload: true,
      fetched: false,
      saving: false,
      publishing: false,
      changed: false,
      filesToUpload: 0,
      waitingForUpload: false,
      waitingForUploadOverlay: null,
      form: {
        fileList: [],
        uploadType: 'publication',
        publicationType: 'book',
        imageType: 'figure',
        title: '',
        description: '',
        keywords: [],
        notes: '',
        creators: [emptyCreator()],
      },
      rules: {
        title: [
          { required: true, message: 'Please input title', trigger: 'blur' },
          { min: 4, message: 'Length should be at least 4', trigger: 'blur' },
        ],
        description: [
          { required: true, message: 'Please input description', trigger: 'blur' },
          { min: 4, message: 'Length should be at least 4', trigger: 'blur' },
        ],
        publicationType: [{ required: true, message: 'Please select type', trigger: 'blur' }],
        imageType: [{ required: true, message: 'Please select type', trigger: 'blur' }],
        embargoDate: [
          {
            type: 'date',
            required: true,
            message: 'Please pick a date',
            trigger: 'change',
          },
        ],
        creators: [
          {
            validator: (rule, value, callback) => {
              console.log('validator', value)
              if (value.length === 1) {
                callback(new Error('Please input at least one author'))
              } else {
                const wrongAuthor = value.some(creator => creator.affiliation && !creator.name)
                if (wrongAuthor) {
                  callback(new Error('Please input a name for each creator'))
                } else {
                  callback()
                }
              }
            },
            trigger: 'blur',
          },
        ],
      },
      uploadChoice: values.choices.uploadType,
      publicationChoice: values.choices.publicationType,
      imageChoice: values.choices.imageType,
      availableKeywords,
      uploadUrl: '',
    }
  },
  mounted() {
    if (this.$route.params.id !== 'new') {
      this.newUpload = false
      const user = this.$cookies.get('user')
      if (user) {
        this.uploadUrl = `${config.baseURL}/deposit/depositions/${
          this.$route.params.id
        }/files?access_token=${user.accessToken}`

        axios
          .get(`/deposit/depositions/${this.$route.params.id}`, constructConfig(user))
          .then(({ data }) => {
            if (typeof data === 'object') {
              const {
                upload_type: uploadType,
                publication_type: publicationType,
                image_type: imageType,
                title = '',
                creators = [],
                description,
                license,
                keywords = [],
                notes = '',
              } = data.metadata

              this.submitted = data.submitted

              this.form = {
                ...this.form,
                uploadType,
                publicationType,
                imageType,
                title,
                description,
                license,
                keywords,
                notes: notes
                  ? notes.replace(values.funding, '').replace(values.funding.substring(2), '')
                  : '',
                creators: [...creators, emptyCreator()],
                files: [],
              }

              axios
                .get(`/deposit/depositions/${this.$route.params.id}/files`, constructConfig(user))
                .then(({ data: files }) => {
                  files.forEach(file => {
                    this.form.files.push({
                      name: file.filename,
                      url: file.links.download,
                      id: file.id,
                    })
                  })
                  this.initialValues = { ...this.form }

                  this.fetched = true
                })
                .catch(error => {
                  if (error.response.data.status === 401) {
                    this.$notify({
                      title: 'Oups',
                      message:
                        "Sorry you don't seem to have the right to do this action. Please sign in again.",
                      type: 'error',
                    })
                  } else {
                    this.$notify({
                      title: 'Oups',
                      message: 'Impossible to retrieve upload',
                      type: 'error',
                    })
                  }
                })
            }
          })
          .catch(error => {
            console.log('error', error)
            if (error.response.data.status === 401) {
              this.$notify({
                title: 'Oups',
                message:
                  "Sorry you don't seem to have the right to do this action. Please sign in again.",
                type: 'error',
              })
            } else {
              console.log('error', error) // eslint-disable-line
              this.$notify({
                title: 'Oups',
                message: 'Impossible to get upload information',
                type: 'error',
              })
            }
          })
          .then(() => {})
      }
    } else {
      this.fetched = true
      this.initialValues = { ...this.form }
      const user = this.$cookies.get('user')
      if (user) {
        this.form.creators.unshift({ name: user.name, affiliation: user.organization })
      }
    }
  },
  methods: {
    confirmPublish() {
      this.$confirm(
        'Once the record is published you will no longer be able to change the files in this upload.',
        'Warning',
        {
          confirmButtonText: 'I understand',
          cancelButtonText: 'Cancel',
          type: 'info',
        },
      )
        .then(() => {
          this.handlePublish()
        })
        .catch(() => {})
    },
    onSave() {
      this.$refs.form.validate(valid => {
        if (!valid) {
          return
        }
        const user = this.$cookies.get('user')
        if (user) {
          this.uploadUrl = `${config.baseURL}/deposit/depositions/${
            this.$route.params.id
          }/files?access_token=${user.accessToken}`
          if (this.$route.params.id === 'new') {
            this.saving = true
            const {
              title,
              description,
              keywords,
              notes,
              uploadType,
              publicationType,
              imageType,
              creators,
            } = this.form

            const newCreators = [...creators]
            newCreators.pop()

            const bonus = {}

            if (uploadType === 'publication') {
              bonus.publication_type = publicationType
            } else if (uploadType === 'image') {
              bonus.image_type = imageType
            }

            axios
              .post(
                '/deposit/depositions',
                {
                  metadata: {
                    ...values.defaultValues,
                    ...bonus,
                    keywords,
                    title,
                    description,
                    access_right: 'open',
                    upload_type: uploadType,
                    notes: `${notes}${values.funding}`,
                    creators: newCreators,
                    communities: [{ identifier: config.community }],
                    publication_date: moment(new Date()).format('YYYY-MM-DD'),
                  },
                },
                constructConfig(user),
              )
              .then(({ data }) => {
                this.saved = true
                this.changed = false
                this.$notify({
                  title: 'Success',
                  message: 'The upload has been successfully saved!',
                  type: 'success',
                })
                this.$router.push(`/upload/${data.id}`)
                this.newUpload = false
                if (this.filesToUpload > 0) {
                  this.waitingForUpload = true
                  this.uploadUrl = `${config.baseURL}/deposit/depositions/${
                    data.id
                  }/files?access_token=${user.accessToken}`
                  setTimeout(() => {
                    this.onStartUpload()
                  }, 2000)
                  this.waitingForUploadOverlay = this.$loading({
                    lock: true,
                    text: 'Upload of files in progress',
                    spinner: 'el-icon-loading',
                    background: 'rgba(0, 0, 0, 0.7)',
                  })
                }
              })
              .catch(error => {
                if (error.response.data.status === 401) {
                  this.$notify({
                    title: 'Oups',
                    message:
                      "Sorry you don't seem to have the right to do this action. Please sign in again.",
                    type: 'error',
                  })
                } else {
                  this.$notify({
                    title: 'Oups',
                    message: 'Error while saving. Please try again later...',
                    type: 'error',
                  })
                }
              })
              .then(() => {
                this.fetched = true
                this.saving = false
              })
          } else {
            this.saving = true

            const {
              title,
              description,
              keywords,
              notes,
              uploadType,
              publicationType,
              imageType,
              creators,
            } = this.form

            const newCreators = [...creators]
            newCreators.pop()

            const bonus = {}

            if (uploadType === 'publication') {
              bonus.publication_type = publicationType
            } else if (uploadType === 'image') {
              bonus.image_type = imageType
            }

            axios
              .put(
                `/deposit/depositions/${this.$route.params.id}`,
                {
                  metadata: {
                    ...bonus,
                    title,
                    description,
                    keywords,
                    access_right: 'open',
                    upload_type: uploadType,
                    notes: `${notes}${values.funding}`,
                    creators: newCreators,
                    communities: [{ identifier: config.community }],
                    language: values.defaultValues.language,
                  },
                },
                constructConfig(user),
              )
              .then((/* { data } */) => {
                this.saved = true
                this.changed = false
                this.$notify({
                  title: 'Success',
                  message: 'The upload has been successfully saved!',
                  type: 'success',
                })
                if (this.filesToUpload > 0) {
                  this.waitingForUpload = true
                  this.onStartUpload()
                  this.waitingForUploadOverlay = this.$loading({
                    lock: true,
                    text: 'Upload of files in progress',
                    spinner: 'el-icon-loading',
                    background: 'rgba(0, 0, 0, 0.7)',
                  })
                }
              })
              .catch(error => {
                if (error.response.data.status === 401) {
                  this.$notify({
                    title: 'Oups',
                    message:
                      "Sorry you don't seem to have the right to do this action. Please sign in again.",
                    type: 'error',
                  })
                } else {
                  this.$notify({
                    title: 'Oups',
                    message: 'Error while saving. Please try again later...',
                    type: 'error',
                  })
                }
              })
              .then(() => {
                this.saving = false
              })
          }
        }
      })
    },
    handlePublish() {
      const user = this.$cookies.get('user')
      if (user) {
        if (this.$route.params.id !== 'new') {
          this.publishing = true
          axios
            .post(
              `/deposit/depositions/${this.$route.params.id}/actions/publish`,
              {},
              constructConfig(user),
            )
            .then((/* { data } */) => {
              this.$notify({
                title: 'Success',
                message: 'The upload has been successfully published!',
                type: 'success',
              })
              this.submitted = true
            })
            .catch(error => {
              if (error.response.data.status === 401) {
                this.$notify({
                  title: 'Oups',
                  message:
                    "Sorry you don't seem to have the right to do this action. Please sign in again.",
                  type: 'error',
                })
              } else {
                this.$notify({
                  title: 'Oups',
                  message: 'Error while publishing. Please try again later...',
                  type: 'error',
                })
              }
            })
            .then(() => {
              this.publishing = false
            })
        }
      }
    },
    handleRemove() {},
    handlePreview(file) {
      if (file.status === 'success') {
        window.open(file.url, '_blank')
      }
    },
    handleUploadSuccess(response, file) {
      this.filesToUpload -= 1
      if (this.filesToUpload === 0) {
        if (this.waitingForUpload) {
          this.waitingForUploadOverlay.close()
        }
        this.$notify({
          title: 'Success',
          message: `The file ${file.name} has been successfully uploaded`,
          type: 'success',
        })
        this.changed = false
      }
    },
    handleUploadBeforeRemove(file) {
      let result = false
      if (file.status === 'ready') {
        result = true
      } else if (file.status === 'success' && file.id) {
        const user = this.$cookies.get('user')
        if (user && this.$route.params.id !== 'new') {
          result = axios.delete(
            `/deposit/depositions/${this.$route.params.id}/files/${file.id}`,
            constructConfig(user),
          )
        }
      }

      return result
    },
    handleUploadError(error, file) {
      console.log('error', error)
      this.$notify({
        title: 'Oups',
        message: `Error while uploading file ${file.name}.`,
        type: 'error',
      })
      if (this.waitingForUpload) {
        this.waitingForUploadOverlay.close()
      }
    },
    handleUploadChange(file) {
      if (file.status === 'ready') {
        this.changed = true
        this.filesToUpload += 1
      }
    },
    removeCreator(index) {
      const newCreators = [...this.form.creators]
      newCreators.splice(index, 1)
      this.form.creators = newCreators
    },
    handleChange(index) {
      if (index === this.form.creators.length - 1) {
        this.form.creators.push(emptyCreator())
      }
    },
    onStartUpload() {
      const user = this.$cookies.get('user')
      if (user) {
        if (this.$route.params.id === 'new') {
          this.saving = true
          axios
            .post('/deposit/depositions', {}, constructConfig(user))
            .then(({ data }) => {
              this.saved = true
              this.uploadUrl = `${config.baseURL}/deposit/depositions/${
                data.id
              }/files?access_token=${user.accessToken}`
              this.$router.push(`/upload/${data.id}`)
              this.newUpload = false
              setTimeout(() => {
                // weird but the uploadUrL needs a certain time to be changed in the DOM
                this.$refs.upload.submit()
              }, 2000)
            })
            .catch(error => {
              if (error.response.data.status === 401) {
                this.$notify({
                  title: 'Oups',
                  message:
                    "Sorry you don't seem to have the right to do this action. Please sign in again.",
                  type: 'error',
                })
              } else {
                this.$notify({
                  title: 'Oups',
                  message: 'Error while saving. Please try again later...',
                  type: 'error',
                })
              }
            })
            .then(() => {
              this.saving = false
            })
        } else {
          this.$refs.upload.submit()
        }
      }
    },
    onDelete() {
      const user = this.$cookies.get('user')
      if (user && this.$route.params.id !== 'new') {
        this.$confirm('This will permanently delete this upload. Continue?', 'Warning', {
          confirmButtonText: 'OK',
          cancelButtonText: 'Cancel',
          type: 'danger',
          callback: action => {
            if (action !== 'confirm') {
              return
            }
            axios
              .delete(`/deposit/depositions/${this.$route.params.id}`, constructConfig(user))
              .then((/* { data } */) => {
                const loading = this.$loading({
                  lock: true,
                  text: 'Delete in progress',
                  spinner: 'el-icon-loading',
                  background: 'rgba(0, 0, 0, 0.7)',
                })
                setTimeout(() => {
                  loading.close()
                  this.$router.push('/upload/list')
                }, 2500)
              })
              .catch(error => {
                if (error.response.data.status === 401) {
                  this.$notify({
                    title: 'Oups',
                    message:
                      "Sorry you don't seem to have the right to do this action. Please sign in again.",
                    type: 'error',
                  })
                } else {
                  this.$notify({
                    title: 'Oups',
                    message: 'Error while saving. Please try again later...',
                    type: 'error',
                  })
                }
              })
              .then(() => {
                this.fetched = true
                this.saving = false
              })
          },
        })
      }
    },
  },
  components: {
    Header,
    Footer,
  },
}
</script>


<style lang="scss">
@import '../themes.scss';

$logoHeight: 70px;
$maxWidth: 900px;

$title: #409eff;

.upload {
  .title {
    text-align: left;
    font-weight: normal;
  }

  .sectionTitle {
    text-align: left;
    font-weight: normal;
    color: $title;
    border-bottom: 1px solid $title;
  }

  .uploadType {
    line-height: 2.2em;
    padding-bottom: 20px;
  }

  .uploadTypeChoice {
    display: inline-flex;
    flex-direction: column-reverse;

    .el-radio__label {
      margin-bottom: 10px !important;
      paddding-left: 0px !important;
    }
  }

  .formfield {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    margin-bottom: 20px;
    .label {
      padding-top: 10px;
      flex: 0 0 150px;
      text-align: right;
      padding-right: 10px;
    }
  }

  .author {
    padding-bottom: 10px;
    text-align: left;
    .half {
      display: inline-block;
      width: 40%;
      box-sizing: border-box;

      &:first-child {
        padding-right: 10px;
      }
    }
  }

  .no-left {
    margin-left: 0px !important;
    text-align: center;
    .el-form-item__error {
      left: 40% !important;
    }
  }

  .fullWidth {
    width: 100%;
  }

  .left {
    text-align: left;
  }

  .buttons {
    padding-bottom: 30px;
    text-align: right;

    .force-left {
      float: left;
    }
  }

  .removeCreator {
    margin-left: 10px;
    background: transparent;
    border: none;
    outline: none;
    font-size: 1.2em;
    opacity: 0.6;
    cursor: pointer;
    &:hover {
      opacity: 1;
    }
  }

  .add-sentence {
    color: #606266;
    padding-top: 10px;
    display: block;
    font-size: 0.9em;
    line-height: 1.3em;
    text-align: left;
    .sentence {
      display: block;
      font-style: italic;
    }
  }

  .start-upload {
    padding-top: 15px;
  }
}
</style>
